import { useEffect, useState } from 'react';
import { FieldPath, useFormContext } from 'react-hook-form';
import { IncomeVerificationType } from '@brighte/validation';
import { css } from '@emotion/css';
import { useLaunchDarklyContextUpdate } from '@hooks';
import { Box } from '@spark-web/box';
import { ContentDialog } from '@spark-web/modal-dialog';
import { Stack } from '@spark-web/stack';
import { useTheme } from '@spark-web/theme';
import { INCOME_VERIFICATION } from '../../utils';
import UploadDocuments, { type VerificationStatus } from './UploadDocuments';

type IncomeOptionProps = {
  name: FieldPath<IncomeVerificationType>;
  isOpen: boolean;
  onToggle: () => void;
  financialProductId: string;
  vendorId: string;
  incomeType?: 'primary' | 'additional';
  onVerificationFailed?: (wasAlreadyFailed: boolean) => void;
};

// Income verification is document-upload only — the bank-statement / CDR options were removed
// (CDR now lives in the standalone OpenBanking flow). This is a thin modal wrapper that always
// renders the document upload, retaining the dialog open/close, busy, and failure-callback logic.
function IncomeVerificationOptions({
  name,
  isOpen,
  onToggle,
  financialProductId,
  vendorId,
  incomeType,
  onVerificationFailed,
}: IncomeOptionProps) {
  const [isDialogBusy, setIsDialogBusy] = useState(false);
  const [uploadVerificationStatus, setUploadVerificationStatus] = useState<VerificationStatus>('idle');
  const [verificationSessionFailureCount, setVerificationSessionFailureCount] = useState(0);
  const { clearErrors } = useFormContext<IncomeVerificationType>();
  const theme = useTheme();

  const updateLDContext = useLaunchDarklyContextUpdate();

  useEffect(() => {
    if (financialProductId && vendorId) {
      updateLDContext({
        financialProduct: { key: financialProductId },
        vendorId: { key: vendorId },
      });
    }
  }, [financialProductId, vendorId]);

  const isFeedbackFlow = incomeType === 'primary';

  useEffect(() => {
    setUploadVerificationStatus('idle');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setIsDialogBusy(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (uploadVerificationStatus === 'failed') {
      setVerificationSessionFailureCount(prev => prev + 1);
    }
  }, [uploadVerificationStatus]);

  const { VERIFY_PRIMARY_INCOME } = INCOME_VERIFICATION;

  const applyVerificationCloseLogic = () => {
    if (!isFeedbackFlow || uploadVerificationStatus !== 'failed') {
      return;
    }
    const wasAlreadyFailed = verificationSessionFailureCount > 1;
    onVerificationFailed?.(wasAlreadyFailed);
    clearErrors(`${name}.documents` as FieldPath<IncomeVerificationType>);
  };

  const handleDialogToggle = () => {
    if (isDialogBusy) {
      return;
    }
    applyVerificationCloseLogic();
    onToggle();
  };

  const handleUploadClose = () => {
    applyVerificationCloseLogic();
    onToggle();
  };

  return (
    <ContentDialog
      title={VERIFY_PRIMARY_INCOME}
      isOpen={isOpen}
      onToggle={handleDialogToggle}
      showCloseButton={!isDialogBusy}
      size="medium"
      scrollable
      padding="none"
      css={{
        width: 'min(900px, 90vw)',
        // ContentDialog hardcodes 24px (xlarge) content padding on its title <header> and body Stack
        // with no prop to change it, so on mobile only we trim both to 16px via the dialog's own css
        // (which lands on an ancestor of the portalled content). Title stays aligned with the body.
        //
        // `padding="none"` above is why that 24/16 is the whole story: the prop's own padding wraps
        // the header and body rather than replacing theirs, so at its 'large' default it stacked —
        // 16+24=40px of inset on desktop, 8+16=24px on mobile.
        [`@media (max-width: ${theme.breakpoint.tablet - 1}px)`]: {
          '& header': { paddingLeft: theme.spacing.large, paddingRight: theme.spacing.large },
          '& header ~ div:last-child': { padding: theme.spacing.large },
        },
      }}
    >
      {/* Full width: at 98% this inset the content a further ~8px a side inside the body's own
          24px, which read as a second layer of padding. */}
      <Box
        className={css({
          width: '100%',
          display: 'flex',
          alignSelf: 'center',
        })}
      >
        <Stack gap="xxlarge" width="full">
          <UploadDocuments
            name={`${name}.documents`}
            closeDialog={handleUploadClose}
            incomeType={incomeType}
            onDialogBusyChange={setIsDialogBusy}
            onVerificationStatusChange={setUploadVerificationStatus}
          />
        </Stack>
      </Box>
    </ContentDialog>
  );
}

export default IncomeVerificationOptions;
